import { HttpResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { lastValueFrom } from 'rxjs'
import { AnonymousData } from '../../rest/decorators/mapped'
import { AuthToken } from '../../rest/models/auth-token'
import { HttpService, ResponseType } from '../../rest/services/http.service'
import { routes } from '../../rest/utilities/api-routes'
import {
  APIResponse,
  ApiValidationResult,
} from '../models/api-validation-result'
import { User } from '../models/user'
import { AuthenticationResult } from '../types/authentication-result'
import { ManagerVersion, parseVersion } from '../types/manager-version'
import { EnvUtilities } from '../utilities/env-utilities'
import { ApiService } from './api.service'

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ApiService {
  protected http = inject(HttpService)

  logIn(email: string, password: string) {
    return this.withSubscription(
      this.http.post<APIResponse<AuthenticationResult>>(
        EnvUtilities.routeUrl(routes.api.management.auth.logIn),
        {
          email,
          password,
        }
      ),
      this.handleAuthenticationResult.bind(this),
      (error) => this.error(error)
    )
  }

  verifyCode(
    method: 'email' | 'authenticator',
    email: string,
    password: string,
    code: string,
    verificationID: string | null
  ) {
    return this.withSubscription(
      this.http.post<APIResponse<AuthenticationResult>>(
        EnvUtilities.routeUrl(routes.api.management.auth.verify),
        {
          method,
          email,
          password,
          code,
          verification: verificationID,
        }
      ),
      this.handleAuthenticationResult.bind(this),
      (error) => this.error(error)
    )
  }

  getSocialUrl() {
    return this.withSubscription(
      this.http.get<APIResponse<{ url: string }>>(
        EnvUtilities.routeUrl(routes.api.management.auth.social.url, {
          driver: 'microsoft',
        })
      ),
      (response) => response.data.url,
      (error) => this.error(error)
    )
  }

  verifySocial(code: string, state: string) {
    return this.withSubscription(
      this.http.post<APIResponse<AuthenticationResult>>(
        EnvUtilities.routeUrl(routes.api.management.auth.social.verify, {
          driver: 'microsoft',
        }),
        {
          code,
          session_state: state,
        }
      ),
      this.handleAuthenticationResult.bind(this),
      (error) => this.error(error)
    )
  }

  lostPassword(email: string, resetURL: string) {
    return this.withSubscription(
      this.http.post<APIResponse>(
        EnvUtilities.routeUrl(routes.api.management.auth.forgotPassword),
        {
          email,
          reset_url: `${resetURL}?token={TOKEN}`,
        }
      ),
      () => true,
      (error) => this.error(error)
    )
  }

  resetPassword(email: string, password: string, token: string) {
    return this.withSubscription(
      this.http.post<APIResponse<AuthenticationResult>>(
        EnvUtilities.routeUrl(routes.api.management.auth.resetPassword),
        {
          email,
          password,
          token,
        }
      ),
      this.handleAuthenticationResult.bind(this),
      (error) => this.error(error)
    )
  }

  authenticatorURL() {
    return this.withSubscription(
      this.http.post<APIResponse<string>>(
        EnvUtilities.routeUrl(routes.api.management.auth.authenticatorUrl)
      ),
      (response) => response.data,
      (error) => this.error(error)
    )
  }

  completeAuthenticatorConfiguration(code: string) {
    return this.withSubscription(
      this.http.post<APIResponse>(
        EnvUtilities.routeUrl(routes.api.management.auth.completeAuthenticator),
        { code }
      ),
      (response) => this.http.fractalItem(response, User.create),
      (error) => this.error(error)
    )
  }

  reset() {
    this.http.setUserToken(null)
  }

  logOut() {
    return this.withSubscription(
      this.http.post<APIResponse>(
        EnvUtilities.routeUrl(routes.api.management.auth.logOut)
      ),
      () => true,
      (error) => this.error(error)
    )
  }

  private handleAuthenticationResult(
    response: APIResponse<AuthenticationResult>
  ) {
    const token = AuthToken.fromResult(response.data)
    if (token) {
      this.http.setUserToken(token)
    }
    return response.data
  }

  async getUser() {
    return new Promise<{ user: User; version: ManagerVersion | null }>(
      (resolve, reject) =>
        this.http
          .get<HttpResponse<{ data: AnonymousData }>>(
            EnvUtilities.routeUrl(routes.api.management.auth.me),
            undefined,
            undefined,
            ResponseType.raw
          )
          .then((observable) => {
            lastValueFrom(observable)
              .then((response) => {
                const version = response.headers.get('x-manager-version')
                resolve({
                  user: this.http.fractalItem(response.body, User.create),
                  version: version ? parseVersion(version) : null,
                })
              })
              .catch((error) => reject(this.errorResult(error)))
          })
          .catch(() =>
            reject(ApiValidationResult.withMessages(['Invalid refresh token']))
          )
    )
  }
}
